// What-if слайдер: «+X к ежемесячной досрочке». Дёргает API при отпускании ползунка.

import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';
import { api } from '../../api/client';
import { formatWithCurrency } from '../../lib/money';

type Props = {
  baseMonthlyPrepayMinor: string;
  currency: string;
  // Шаг в основных единицах (например, 100_000 сум).
  stepMajor?: number;
  maxExtraMajor?: number;
};

export function WhatIfSlider({
  baseMonthlyPrepayMinor,
  currency,
  stepMajor = 100_000,
  maxExtraMajor = 5_000_000,
}: Props): JSX.Element {
  const [extraMajor, setExtraMajor] = useState(0);
  const deferredExtra = useDeferredValue(extraMajor);
  const extraMinor = BigInt(deferredExtra) * 100n;

  const wiQ = useQuery({
    queryKey: ['debt-whatif', deferredExtra],
    queryFn: () => api.debtWhatIf(extraMinor, 'avalanche'),
    staleTime: 30_000,
  });

  const baseLabel = formatWithCurrency(baseMonthlyPrepayMinor, currency);
  const extraLabel = formatWithCurrency(extraMinor, currency);

  const data = wiQ.data;
  const monthsSaved = data?.months_saved ?? null;
  const interestSaved = data ? BigInt(data.interest_saved_minor) : 0n;
  const newMonths = data?.whatif_months_to_close ?? null;
  const baseMonths = data?.baseline_months_to_close ?? null;

  return (
    <div className="whatif">
      <div className="whatif-base">
        Базовая досрочка: <b>{baseLabel}</b> в месяц
      </div>
      <div className="whatif-extra">
        Добавить ещё: <b>+{extraLabel}</b>
      </div>
      <input
        type="range"
        className="whatif-slider"
        min={0}
        max={maxExtraMajor}
        step={stepMajor}
        value={extraMajor}
        onChange={(e) => setExtraMajor(Number(e.target.value))}
      />
      <div className="whatif-result">
        {data ? (
          deferredExtra === 0 ? (
            <>
              При текущей досрочке {baseLabel} долги закроются за{' '}
              <b>{baseMonths != null ? `${baseMonths} мес` : '—'}</b>.
            </>
          ) : monthsSaved != null && monthsSaved > 0 ? (
            <>
              Закроешь на <b>{monthsSaved} мес раньше</b> и сэкономишь ~
              <b>{formatWithCurrency(interestSaved > 0n ? interestSaved : 0n, currency)}</b>{' '}
              процентов. Новый срок: {newMonths} мес.
            </>
          ) : (
            <>Эффект минимальный. Попробуй большую сумму.</>
          )
        ) : (
          <>Считаем сценарий…</>
        )}
      </div>
    </div>
  );
}
